import { useEffect } from 'react';
import { Alert, Text } from '@nimbus-ds/components';
import { useSelector } from 'react-redux';
import { PaymentProviderDto } from '@tiendanube/common';
import { Country } from '@tiendanube/common/src/enums';
import { useAppDispatch } from 'App/store';
import { Stack } from 'commons/components';
import { dateFormat, Format } from 'commons/utils/date';
import { useGetCountry } from 'domains/Auth/hooks';
import {
  getRecipientAction,
  getRecipientData,
} from 'domains/PagoNubeOnboarding/Recipient/recipientSlice';
import { useStoreInfo } from 'domains/PartnersApps/hooks';
import useTranslationPayments from 'domains/Payments/useTranslationPayments';

interface PaymentProviderStatusAlertProps {
  paymentProvider: PaymentProviderDto;
}

const getAlertMessage = (
  paymentProvider: PaymentProviderDto,
  missingRequiredFields?: string[],
) => {
  if (paymentProvider.processingInstallation) {
    return 'personalDataAnalysis';
  }

  if (paymentProvider.mustCompleteInstallation && missingRequiredFields) {
    const isMissingDocuments = missingRequiredFields.includes('documents');
    if (missingRequiredFields.length === 1 && isMissingDocuments) {
      return 'pendingDocument';
    }

    if (missingRequiredFields.length > 1 || !isMissingDocuments) {
      return 'pendingFormCompletion';
    }
  }

  return '';
};

export function PaymentProviderStatusAlert({
  paymentProvider,
}: PaymentProviderStatusAlertProps) {
  const dispatch = useAppDispatch();
  const recipientData = useSelector(getRecipientData);
  const recipientKyc = recipientData?.kyc;
  const country = useGetCountry();
  const t = useTranslationPayments();
  const { id: storeId } = useStoreInfo();

  useEffect(() => {
    if (!recipientData && country === Country.MX && storeId) {
      dispatch(getRecipientAction({ storeId }));
    }
  }, [dispatch, recipientData, country, storeId]);

  if (!recipientData) {
    return null;
  }

  const message = getAlertMessage(
    paymentProvider,
    recipientKyc?.missingRequiredFields,
  );

  if (message) {
    const date = dateFormat(
      recipientKyc?.tosAcceptanceDate || new Date().toDateString(),
      Format.DD_MM,
    );
    return (
      <Alert appearance="warning">
        <Stack column spacing="none" align="flex-start">
          <Text fontWeight="bold">
            {t(`paymentProviderBody.${message}.title`)}
          </Text>
          <Text>
            {t(`paymentProviderBody.${message}.body`, {
              date,
              interpolation: { escapeValue: false },
            })}
          </Text>
        </Stack>
      </Alert>
    );
  }

  return null;
}
